// src/processors/style/style-processor.ts - Main Style Processor
import { XConVitePluginOptions } from '../../types';
import { StyleContentProcessor } from './style-content-processor';
import { StyleFileHandler } from './style-file-handler';
import { StyleUrlMatcher } from './style-url-matcher';
import { Logger } from "../../utils/logger";
import { ProcessResult } from "../../types";

/**
 * Main style processor that orchestrates all style processing operations
 */
export class StyleProcessor {
    private contentProcessor: StyleContentProcessor;
    private fileHandler: StyleFileHandler;
    private urlMatcher: StyleUrlMatcher;

    constructor(
        private options: Required<XConVitePluginOptions>,
        private logger: Logger,
        private terser?: any
    ) {
        this.contentProcessor = new StyleContentProcessor(options, logger, terser);
        this.fileHandler = new StyleFileHandler(options, logger);
        this.urlMatcher = new StyleUrlMatcher();
    }

    /**
     * Process styleUrls -> styles array conversion
     */
    processStyleUrls(
        code: string,
        id: string,
        addWatchFile: (file: string) => void
    ): ProcessResult {
        // If inline styles already exist, skip styleUrls processing
        if (this.hasInlineStyles(code)) {
            this.logger.debug('Inline styles found, skipping styleUrls processing');
            return { code, changed: false };
        }

        let transformedCode = code;
        let hasChanges = false;

        transformedCode = transformedCode.replace(
            /styleUrls:\s*\[([\s\S]*?)\]/g,
            (match, styleUrlsContent, offset) => {
                // Skip if match is within a comment
                if (this.isInComment(code, offset)) {
                    this.logger.debug(`Skipping commented styleUrls`);
                    return match;
                }

                try {
                    const urlMatches = this.urlMatcher.extractStyleUrls(styleUrlsContent);

                    if (urlMatches && urlMatches.length > 0) {
                        const styles = this.processStyleUrlMatches(urlMatches, id, addWatchFile);
                        hasChanges = true;
                        return `styles: [${styles.join(', ')}]`;
                    }

                    return 'styles: []';
                } catch (error) {
                    this.logger.error(`Style error:`, error);
                    return 'styles: []';
                }
            }
        );

        return { code: transformedCode, changed: hasChanges };
    }

    /**
     * Check if inline styles already exist in the code (not in comments)
     */
    private hasInlineStyles(code: string): boolean {
        const stylesRegex = /styles:\s*\[/g;
        let match;

        while ((match = stylesRegex.exec(code)) !== null) {
            // If we find styles that's not in a comment, return true
            if (!this.isInComment(code, match.index)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Check if a position in code is within a comment
     */
    private isInComment(code: string, position: number): boolean {
        const beforeMatch = code.substring(0, position);
        const lines = beforeMatch.split('\n');
        const currentLine = lines[lines.length - 1];

        // Check for single-line comment
        const singleLineCommentIndex = currentLine.indexOf('//');
        if (singleLineCommentIndex !== -1 && singleLineCommentIndex < currentLine.length) {
            return true;
        }

        // Check for multi-line comment
        const lastBlockCommentStart = beforeMatch.lastIndexOf('/*');
        const lastBlockCommentEnd = beforeMatch.lastIndexOf('*/');

        if (lastBlockCommentStart !== -1 && lastBlockCommentStart > lastBlockCommentEnd) {
            return true;
        }

        return false;
    }

    /**
     * Process individual style URL matches
     */
    private processStyleUrlMatches(
        urlMatches: string[],
        id: string,
        addWatchFile: (file: string) => void
    ): string[] {
        const styles: string[] = [];

        urlMatches.forEach((urlMatch: string) => {
            const stylePath = this.urlMatcher.extractPathFromUrl(urlMatch);
            const styleResult = this.fileHandler.loadStyleFile(stylePath, id, addWatchFile);

            if (styleResult !== null) {
                const { content, fullPath } = styleResult;
                const processedContent = this.contentProcessor.processStyleContent(content, fullPath);
                styles.push(`'${processedContent}'`);
                this.logger.debug(`Loaded style: ${stylePath}`);
            } else {
                this.logger.warn(`Style file not found: ${stylePath}`);
            }
        });

        return styles;
    }
}